import { propOr } from 'ramda'
import { useTranslation } from 'react-i18next'

import { useOpenModal } from '../../../components/modals/ModalsContext'
import { useReloadLoggedUser } from '../../session/reloadLoggedUser'
import { shouldOpenCampaignNow } from '../../../containers/typeform/_helpers/campaign'

// Users ------------------------------
export const useOpenUserContractModal = () => {
  const openModal = useOpenModal()
  return item => openModal('contracts/show', item.id, item.name)
}

export const useOpenUserModal = () => {
  const openModal = useOpenModal()
  return item => openModal('users/show', item.id, item.name)
}

export const useOpenEditUserModal = options => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return ({ id, onSave, initialState } = {}) =>
    openModal(
      'users/edit',
      { id, onSave, initialState },
      id ? t('modals.editingUser') : t('modals.creatingUser'),
      options
    )
}

export const useOpenChangePasswordModal = options => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return id => openModal('users/changePassword', { id }, t('modals.changingPassword'), options)
}

export const useOpenEditUsersModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return ids =>
    openModal('users/bulkEdit', ids, !!ids?.length ? t('modals.editingUsers') : t('modals.registeringUsers'))
}

// Groups ------------------------------

export const useOpenEditGroupModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return item => openModal('groups/edit', item?.id, item?.id ? t('modals.editGroup') : t('modals.createGroup'))
}

// Checklist type ------------------------------

export const useOpenEditChecklistTypeModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return item =>
    openModal(
      'checklistType/edit',
      item?.id,
      item?.id ? t('modals.editChecklistCategory') : t('modals.createChecklistCategory')
    )
}

// Minicruds ------------------------------

export const useOpenEditMinicrudModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data => openModal('minicruds/edit', data, data?.id ? t('modals.editMinicrud') : t('modals.createMinicrud'))
}

// Places ------------------------------

export const useOpenEditPlaceModal = options => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return ({ id, onSave, initialState }) =>
    openModal(
      'places/edit',
      { id, onSave, initialState },
      id ? t('modals.editingPlace') : t('modals.creatingPlace'),
      options
    )
}

export const useOpenPlaceModal = () => {
  const openModal = useOpenModal()
  return item => openModal('places/show', item.id, item.name)
}

export const useOpenPlaceLevelSettingsModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return item => openModal('places/levelSettings', item, t('modals.customizeLevels'))
}

export const useOpenEditPlaceLevelModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data =>
    openModal('placeLevels/edit', data, data.id ? t('modals.editingPlaceLevel') : t('modals.creatingPlaceLevel'))
}

export const useOpenEditTagModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data => openModal('tags/edit', data, data?.id ? t('modals.editingTag') : t('modals.creatingTag'))
}

export const useOpenTagsModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return () => openModal('tags/list', {}, t('modals.tagsList'))
}

export const useOpenEditPlaceTypesModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return key =>
    openModal('placeTypes/edit', { key }, key ? t('modals.editingPlaceType') : t('modals.creatingPlaceType'))
}

export const useOpenEditPlacesModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data => openModal('places/bulkEdit', data, t('modals.registeringItems'))
}

// Availability ------------------------------

export const useOpenAvailabilityModal = () => {
  const openModal = useOpenModal()
  return item => openModal('availability/show', item.id, item.name)
}

export const useOpenChangeAvailabilityStatusModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('availability')
  return ({ placeId, placeName, currentStatus }) =>
    openModal(
      'availability/changeStatus',
      { placeId, currentStatus },
      placeName || t('modals.changeAvailabilityStatus')
    )
}

export const useOpenAvailabilityStatusSourceConfigModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return () => openModal('availability/statusSourceConfig', {}, t('modals.availabilityStatusSourceConfig'))
}

// Profiles ------------------------------

export const useOpenEditProfileModal = options => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return id => openModal('profiles/edit', id, id ? t('modals.editingProfile') : t('modals.creatingProfile'), options)
}

// Processes ------------------------------

export const useOpenEditProcessModal = options => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data =>
    openModal(
      'processes/edit',
      data,
      data ? (data.isCloning ? t('modals.cloningProcess') : t('modals.editingProcess')) : t('modals.creatingProcess'),
      options
    )
}

// Screens ------------------------------

export const useOpenEditScreenModal = options => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data =>
    openModal(
      'screens/edit',
      data,
      data ? (data.isCloning ? t('modals.cloningScreen') : t('modals.editingScreen')) : t('modals.creatingScreen'),
      options
    )
}

export const useOpenEditScreenKanbanColumnModal = options => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data =>
    openModal(
      'screens/kanbanColumn/edit',
      data,
      data.isCloning ? t('modals.cloningColumn') : data.index ? t('modals.editingColumn') : t('modals.creatingColumn'),
      options
    )
}

export const useOpenEditWidgetModal = options => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data => openModal('screens/widget/edit', data, t('modals.editingWidget'), options)
}

export const useOpenSelectWidgetTypeModal = options => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data => openModal('screens/widget/selectType', data, t('modals.addingWidget'), options)
}

// Service Orders ------------------------------

export const useOpenServiceOrderModal = () => {
  const openModal = useOpenModal()
  return item => openModal('serviceOrders/show', item.id, item.yearlyId)
}

export const useOpenEditServiceOrdersUserModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return item => openModal('serviceOrders/editUser', item.id, t('modals.editingServiceOrderUser'))
}

export const useOpenEditServiceOrdersStatusModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data => openModal('serviceOrders/editStatus', data, t('modals.editingStatus'))
}

export const useOpenExportSelectedServiceOrdersModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data => {
    return openModal(
      'exports/new',
      {
        entity: 'serviceOrder',
        serviceOrderIds: data?.ids,
        filter: { ids: data?.ids }
      },
      t('modals.exportingServiceOrders')
    )
  }
}

export const useOpenExecuteServiceOrderModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return item => openModal('serviceOrders/execution', item.id, t('modals.replyingServiceOrder'))
}

// Abre a MESMA tela de execução/edição de atividade, só que em modo "em
// massa": em vez de um id único, o loader recebe a lista de ids selecionados
// e passa a reaproveitar toda a lógica existente de preenchimento/conclusão.
export const useOpenBulkExecuteServiceOrdersModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return ids => openModal('serviceOrders/execution', { bulkServiceOrderIds: ids }, t('modals.replyingServiceOrder'))
}

export const useOpenServiceOrderResumeModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return item => openModal('serviceOrders/resume', item.id, t('modals.serviceOrderResume'))
}

export const useOpenCloneServiceOrderModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return item => openModal('serviceOrders/clone', propOr(item, 'id', item), t('modals.cloningServiceOrder'))
}

export const useOpenRemoveModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data => openModal('serviceOrders/remove', data || {}, t('serviceOrders.removeActivities'))
}

export const useOpenEditServiceOrderModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data =>
    openModal('serviceOrders/edit', data, data?.id ? t('modals.editingServiceOrder') : t('modals.creatingServiceOrder'))
}

export const useOpenEditServiceOrdersModal = parent => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return ({ ids, placeIds, placeType }) =>
    openModal(
      'serviceOrders/bulkEdit',
      { ids, placeIds, placeType, parent },
      !!ids?.length ? t('modals.editingServiceOrders') : t('modals.registeringServiceOrders')
    )
}

export const useOpenMailPublicExecutionModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return ids => openModal('serviceOrders/mailPublicExecution', ids, t('modals.sendingPublicExecutionLink'))
}

// Replies ------------------------------

export const useOpenRepliesModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data => {
    openModal('replies/list', data, t('modals.replies'))
  }
}

// Dynamic Service Orders ------------------------------

export const useOpenDynamicServiceOrdersModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data =>
    openModal(
      'dynamicServiceOrders/edit',
      data,
      data?.id ? t('modals.editingDynamicServiceOrders') : t('modals.creatingDynamicServiceOrders')
    )
}

// Stocks ------------------------------
export const useOpenStocksModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data =>
    openModal('stockItem/edit', data, data?.id ? t('modals.editingStockItem') : t('modals.creatingStockItem'))
}

export const useOpenStockItemDetailModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data => openModal('stockItem/show', data, t('modals.details'))
}

export const useOpenTransferStockItemsModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return () => openModal('stockItems/bulkTransfers', {}, t('modals.transfers'))
}

export const useOpenTransferStockItemHistoryModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return stockItemId => openModal('stockItem/transfersHistory', { id: stockItemId }, t('modals.transferHistory'))
}

export const useOpenDetailsStockModal = () => {
  const openModal = useOpenModal()
  return ({ id }) => openModal('stockItem/show', { id })
}

// Forms ------------------------------

export const useOpenEditFormModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return id => openModal('forms/edit', id, t('modals.formEdit'))
}

export const useOpenEditFormsModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return () => openModal('forms/bulkEdit', null, t('modals.formsEdit'))
}

export const useOpenFormReplyModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return ({ formId, number }) => openModal('formReplies/show', { formId, number }, t('modals.formReply'))
}

export const useOpenFormReplyServiceOrdersModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return id => openModal('formReplies/serviceOrders', id, t('modals.actionPlans'))
}

// Assets ------------------------------

export const useOpenAssetModal = () => {
  const openModal = useOpenModal()
  return item => openModal('asset/show', item.id, item.name)
}

export const useOpenEditAssetsModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return ids => openModal('asset/bulkEdit', ids, t('modals.registeringItems'))
}

export const useOpenEditAssetModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return id => openModal('asset/edit', id, id ? t('modals.editingAsset') : t('modals.creatingAsset'))
}

// Masks ------------------------------

export const useOpenMaskModal = () => {
  const openModal = useOpenModal()
  return item => openModal('masks/show', item.id, item.name)
}

export const useOpenEditMaskModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return id => openModal('masks/edit', id, id ? t('modals.editingAsset') : t('modals.creatingAsset'))
}

// Templates ------------------------------

export const useOpenTemplatesModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return entity => openModal('templates/list', entity, t('modals.templatesList'))
}

export const useOpenEditTemplatesModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return ({ id, entity }) =>
    openModal('templates/edit', { id, entity }, id ? t('modals.editingTemplate') : t('modals.creatingTemplate'))
}

export const useOpenDetailsTemplatesModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return item => openModal('templates/show', item.id, t('modals.templateDetails'))
}

export const useOpenCreateTemplateRecordModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data => openModal('templateRecords/create', data, t('modals.documentGeneration'))
}

export const useOpenTemplatesRecordHistoryModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return item => openModal('templateRecords/list', item, t('modals.documentsHistory'))
}

// Checklists ------------------------------

export const useOpenChecklistModal = () => {
  const openModal = useOpenModal()
  return item => openModal('checklists/show', item.id, item.name)
}

export const useOpenChecklistPreviewModal = () => {
  const openModal = useOpenModal()
  return item => openModal('checklists/preview', item.id, item.name)
}

export const useOpenCloneChecklistModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data => openModal('checklists/clone', data, t('modals.cloningChecklist'))
}

export const useOpenChecklistTemplatesModal = options => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data => openModal('checklists/templates', data, t('modals.chooseTemplate'), options)
}

// Triggers ------------------------------

export const useOpenTriggersModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data => openModal('triggers/list', data || {}, t('modals.automations'), { clearHistory: true })
}

export const useOpenTriggersTemplatesModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data => openModal('triggers/templates', data || {}, t('modals.templates'))
}

export const useOpenTriggersDetailsModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return id => openModal('triggers/show', id || {}, t('modals.automationDetails'))
}

export const useOpenEditTriggerModal = options => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return (data = {}) =>
    openModal('triggers/edit', data, data.id ? t('modals.editingAutomation') : t('modals.creatingAutomation'), options)
}

export const useOpenTriggerCallModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return item => openModal('triggerCalls/show', item.id, t('modals.triggerFired'))
}

// LinkTrees ------------------------------

export const useOpenEditLinktreeItemsModal = () => {
  const openModal = useOpenModal()
  return item => openModal('linkTreeItems/edit', { id: item.id, entity: item.entity }, item ? item.name : '')
}

export const useOpenLinktreesModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data => openModal('linkTrees/list', data, t('modals.qrCodeMenus'))
}

export const useOpenEditLinktreeModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data => openModal('linkTrees/edit', data, data.id ? t('modals.editingLinktree') : t('modals.creatingLinktree'))
}

// PublicToken ------------------------------

export const useOpenPublicTokensModal = options => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return item => openModal('publicTokens/list', item, t('modals.publicLinks'), options)
}

export const useOpenEditPublicTokenModal = options => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return item =>
    openModal('publicTokens/edit', item, item.id ? t('modals.editPublicToken') : t('modals.newPublicToken'), options)
}

export const useOpenPublicTokenModal = options => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data => openModal('publicTokens/show', data, t('modals.publicToken'), options)
}

// Integrations ------------------------------

export const useOpenEditIntegrationModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return item => openModal('integrations/edit', item, t('modals.newIntegration'))
}

export const useOpenSelectIntegrationServiceModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return item => openModal('integrations/selectService', item, t('modals.newIntegration'))
}

export const useOpenSelectIntegrationModal = () => {
  const openModal = useOpenModal()
  return item => openModal('integrations/select', item, item.service)
}

export const useOpenEditIntegrationTokenModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return item => openModal('integrations/new/token', item, t('modals.newAccount'))
}

// webhoook ------------------------------
export const useOpenWebhookModalEdit = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return item => openModal('webhook/edit', item, item?.id ? t('modals.editingWebhook') : t('modals.creatingWebhook'))
}

export const useOpenEditWebhookStaysModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return item => openModal('webhook/edit/stays', item, t('modals.newConfiguration'))
}

export const useOpenShowWebhookModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return item => openModal('webhook/show', item, t('modals.webhookDetails'))
}

// Routines ------------------------------

export const useOpenRoutinesModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data => openModal('routines/list', data || {}, t('modals.routines'), { clearHistory: true })
}

export const useOpenEditRoutineModal = options => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return (data = {}) =>
    openModal('routines/edit', data, data.id ? t('modals.editingRoutine') : t('modals.creatingRoutine'), options)
}

// Rest ------------------------------

export const useOpenEditPermissionsModal = () => {
  const openModal = useOpenModal()
  const reloadLoggedUser = useReloadLoggedUser()
  const { t } = useTranslation('common')
  return () => openModal('permissions/edit', { onClose: reloadLoggedUser }, t('modals.editingPermissions'))
}

export const useOpenNonComplianceModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return item => openModal('nonCompliancesV2/show', item.id, item.place?.name || t('modals.nonCompliance'))
}

export const useOpenNonComplianceBulkEditModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return ids => openModal('nonCompliancesV2/bulkEdit', ids, t('modals.nonComplianceBulkEdit'))
}

export const useOpenEditActionPlanModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return params => openModal('actionPlans/edit', params, t('modals.actionPlan'), { outside: true })
}

export const useOpenImportHistoryModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return entity => openModal('imports/history', entity, t('modals.imports'))
}

export const useOpenExportModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data => openModal('exports/new', data, t('modals.export'))
}

export const useOpenExportHistoryModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return entity => openModal('exports/history', entity, t('modals.exports'))
}

export const useOpenBucketsModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data => openModal('buckets/list', data || {}, t('modals.buckets'))
}

export const useOpenEditBucketModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return ({ type }) => openModal('buckets/edit', { type }, t('modals.newBucket'))
}

export const useOpenLogModal = () => {
  const openModal = useOpenModal()
  return data => openModal('logs/show', data || {}, data?.name)
}

export const useOpenSegmentModal = () => {
  const openModal = useOpenModal()
  return item => openModal('segments/show', item.id, item.name)
}

export const useOpenEditSegmentsModal = () => {
  const openModal = useOpenModal()
  return ({ ids }) =>
    openModal('segments/bulkEdit', { ids }, `${!!ids?.length ? 'Editando' : 'Cadastrando'} segmentações`)
}

export const useOpenEditProjectModal = () => {
  const { t } = useTranslation('projects')
  const openModal = useOpenModal()
  return item => openModal('project/edit', item, item.id ? `${t('project.edit')} ${item.name}` : t('project.create'))
}

export const useOpenProjectTemplatesModal = options => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data => openModal('projects/templates', data, t('modals.chooseTemplate'), options)
}

export const useOpenEditScreenViewModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return item => openModal('screenViews/edit', item, t('modals.editingView'))
}

export const useOpenEditFieldsModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return origin => openModal('fields/edit', origin, t('modals.editingFields'))
}

export const useOpenCreateStrongPasswordModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return () => openModal('password/new', {}, t('modals.createStrongPassword'), { disableUrl: true, outside: true })
}

export const useAlertModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return (title, message, options = {}) =>
    openModal('alert', { title, message, ...options }, t('modals.alert'), { outside: true })
}

export const useOpenFormInviteHistoryModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return formId => openModal('formInvites/list', formId, t('modals.formInvites'))
}

export const useOpenSemaphoreConfigModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data => openModal('configs/editSemaphore', data, t('modals.semaphoreConfig'))
}

export const useOpenServiceOrderAutomaticDistributionListModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data => openModal('serviceOrderAutomaticDistribution/list', data, t('modals.automaticDistribution'))
}

export const useOpenServiceOrderAutomaticDistributionEditModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return ({ id }) => openModal('serviceOrderAutomaticDistribution/edit', { id }, t('modals.automaticDistribution'))
}

export const useOpenTypeFormModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data => {
    const shouldOpen = data?.forceToOpen || shouldOpenCampaignNow(data?.campaignId)
    shouldOpen && openModal('typeform', data, t('modals.survey'))
  }
}

export const useOpenGenerateActivatedQRCodesModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return data =>
    openModal('qrcodes', data, data?.ids?.length > 1 ? t('modals.generatingQrCodes') : t('modals.generatingQrCode'))
}

export const useOpenGroupedMapPoints = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return items => openModal('map/groupedDetail', items, t('modals.groupedItems'), { ignoreQueryParams: true })
}

export const useOpenAddSegwareTechnicianModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return () => openModal('integrations/addSegwareTechnician', {}, t('modals.newTechnician'))
}

export const useSegwareTechnicianModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('common')
  return () => openModal('integrations/listSegwareTechnicianAssociated', {}, t('modals.registeredTechnicians'))
}

export const useOpenSlaConfigModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('listSla')
  return data => openModal('configs/listSla', data, t('modal.listTitle'))
}

export const useOpenSlaConfigEditModal = () => {
  const openModal = useOpenModal()
  const { t } = useTranslation('listSla')
  return ({ id }) => openModal('configs/editSla', { id }, id ? t('modal.editTitle') : t('modal.newTitle'))
}
